import React from "react";
import moment from "moment";
import {
  checkIsLufthansaByClaimType,
  isBaggageOrLostWagesClaim,
} from "../../../../../../components/utils/airlineUtils.js";

const PowerOfAttorneyContent = React.forwardRef((props, ref) => {
  const { formValues } = props;

  if (!formValues) return null;

  // Destructure from RHF data
  const {
    personal_details,
    travelers,
    original_flight_details,
    actual_flight_details,
    type_of_claim,
    isLufthansa: propIsLufthansa,
  } = formValues;

  const isLufthansa = Boolean(
    propIsLufthansa !== undefined
      ? propIsLufthansa
      : checkIsLufthansaByClaimType({
          typeOfClaim: type_of_claim,
          originalFlights: original_flight_details,
          actualFlights: actual_flight_details,
        })
  );

  // Map RHF fields to what the POA template expects
  const current_date = moment().format("MM/DD/YYYY");

  const clientName = `${personal_details?.name_prefix ? personal_details.name_prefix + " " : ""}${personal_details?.first_name || ""} ${personal_details?.last_name || ""}`.trim();
  const clientBirthDate = personal_details?.date_of_birth;
  const passportNumber = personal_details?.passport_number;

  const familyMembers = travelers?.filter(t => t.first_name || t.last_name) || [];

  // Flight routing strictly based on claim type without OR fallbacks
  const flightDetails = isBaggageOrLostWagesClaim(type_of_claim)
    ? actual_flight_details || []
    : original_flight_details || [];

  const formatDOB = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const formats = [
      "DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD",
      "DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD",
      "DD.MM.YYYY", "MM.DD.YYYY",
    ];

    const parsed = moment(dateOfBirth, formats, true);

    if (!parsed.isValid()) {
      return dateOfBirth; // Fallback to raw string
    }
    return parsed.format("MM-DD-YYYY");
  };

  function getClaimType(type) {
    switch (type) {
      case "overbooking":
      case "denied_boarding": return "Denied boarding";
      case "lost_baggage": return "Lost luggage";
      case "damage_baggage": return "Damaged luggage";
      case "travel_expenses": return "Travel expenses";
      case "delayed_baggage": return "Delayed luggage";
      case "delayed": return "Delayed";
      case "cancelled": return "Cancelled";
      case "lost/damaged": return "Lost/Damaged";
      case "lost_wages": return "Lost Wages";
      case "other":
      case "others": return "Others";
      default: return type || "Flight Claim";
    }
  }

  const renderClaimText = (flight) => {
    const airlineName =
      (typeof flight?.airline === "string" ? flight.airline : flight?.airline?.label || flight?.airline?.airline_name) ||
      (typeof flight?.actual_airline === "string" ? flight.actual_airline : flight?.actual_airline?.label || flight?.actual_airline?.airline_name) ||
      (isLufthansa ? "Lufthansa" : "the airline");
    const flightNum = flight?.flight_number || flight?.actual_flight_number || "";
    const flightDate = formatDOB(flight?.date_of_travel || flight?.actual_date_of_travel);
    const flightNumAndDate = [flightNum, flightDate].filter(Boolean).join(" and ");

    return (
      <>
        to pursue claims on my behalf against {airlineName}{" "}
        for compensation for:
        <ol>
          <li>
            {getClaimType(type_of_claim)} flight ({flightNumAndDate})
          </li>
        </ol>
        increased by potential claims for damages suffered and interest for late payment (the <strong>“Claims”</strong>).
      </>
    );
  };

  const FlightDetailsList = () => {
    if (flightDetails.length > 1) {
      return (
        <div style={{ paddingLeft: "20px" }}>
          <ul style={{ listStyleType: "none", paddingLeft: "20px" }}>
            {flightDetails.map((flight, index) => (
              <React.Fragment key={index}>
                <li>{renderClaimText(flight)}</li>
                {index < flightDetails.length - 1 && <br />}
              </React.Fragment>
            ))}
          </ul>
        </div>
      );
    } else if (flightDetails.length === 1 && (flightDetails[0].flight_number || flightDetails[0].actual_flight_number)) {
      const flight = flightDetails[0];
      return <p>{renderClaimText(flight)}</p>;
    } else {
      return <p>No flight details available.</p>;
    }
  };

  return (
    <div
      style={{
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        ...(props.isModal
          ? {
              position: "relative",
              width: "100%",
              maxWidth: "800px",
              margin: "0 auto",
              padding: "16px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxSizing: "border-box",
            }
          : {
              position: "absolute",
              left: "-9999px",
              top: "-9999px",
              width: "800px",
              minHeight: "1122px",
              margin: "0 auto",
              padding: "16px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxSizing: "border-box",
            }),
      }}
      ref={ref}
      className={`print-container`}
    >
      <h1 style={{ textAlign: "center", marginBottom: "24px", fontSize: "18px", fontWeight: "600" }}>
        POWER OF ATTORNEY
      </h1>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ textAlign: "left", marginBottom: "16px" }}>{isLufthansa ? current_date : `Date: ${current_date}`}</p>
      </div>
      
      <p>I/We, the undersigned,</p>

      <ol className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        {familyMembers.map((member, index) => (
          <li key={index}>
            {member.name_prefix ? `${member.name_prefix} ` : ""}{member.first_name} {member.last_name}, born {formatDOB(member.date_of_birth)},
            passport number {member.passport_number}, ticket number{" "}
            {member.tickets}
          </li>
        ))}
      </ol>

      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        <strong>Hereby authorize:</strong>
      </p>

      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        {clientName}, born {formatDOB(clientBirthDate)}, passport number {passportNumber} (
        <strong>“Representative”</strong>)
      </p>

      <div className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        <FlightDetailsList />
      </div>

      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        The power of attorney covers all Claims (existing and potential) that I may have against
        the airline related to the above flight. The Representative may conduct all legal and
        factual actions on my behalf (including to assign my claims to Fly And Claim Inc, 11 Main
        Street, Monsey NY 10952, USA and Fly and Claim Travel Services Sp. z o.o., ul. Mogilska
        43, 31-545 Kraków, Poland, KRS: 0001247476, NIP: 6751829771, REGON:
        54500165800000), supply necessary documents, and receive correspondence. The
        Representative may also receive compensation on my behalf.
      </p>

      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        The power of attorney should be interpreted broadly so as to cover all actions necessary
        to achieve its aim. The Representative may grant further powers of attorney.
      </p>

      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        The Representative may pursue the claims on any legal basis and in any
        jurisdiction/country that may be applicable.
      </p>
      <p className={`${familyMembers.length > 2 ? "avoid-break" : ""}`}>
        The power of attorney is subject to {isLufthansa ? "German" : "Polish"} law.
      </p>

      <hr style={{ margin: "20px 0" }} />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        {familyMembers.map((member, index) => (
          <div
            key={index}
            style={{
              flex: "1 1 45%",
              textAlign: "center",
              marginTop: "20px",
            }}
            className="avoid-break"
          >
            <div
              style={{
                width: "300px",
                height: "60px",
                margin: "0 auto",
                marginBottom: "10px",
              }}
            ></div>
            <p>_____________________________________</p>
            <p className="name-text" style={{ textTransform: "uppercase" }}>
              {member.name_prefix ? `${member.name_prefix} ` : ""}{member.first_name} {member.last_name}
            </p>
          </div>
        ))}
      </div>

        <>
          <hr style={{ margin: "20px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "bold",
            }}
            className={familyMembers.length > 3 ? "page-break" : ""}
          >
            <div style={{ flex: "1" }}>
              <p>Foglar Law Firm (NIP: 7343586718)</p>
            </div>
            <div style={{ flex: "1", textAlign: "center" }}>
              <p>www.foglar.pl</p>
            </div>
            <div style={{ flex: "1", textAlign: "right" }}>
              <p>claims@foglar.pl</p>
            </div>
          </div>

          <p>
            Note: Please email the signed document to <a href="mailto:claims@flyandclaim.com">claims@flyandclaim.com</a>
          </p>
        </>
    </div>
  );
});

export default PowerOfAttorneyContent;